/**
 * Content slider controller.
 *
 * Supports lazy single-view slides and translated multi-view tracks while
 * keeping every instance isolated and safe across Theme Editor lifecycles.
 */
(function() {
  'use strict';

  var MANAGER_KEY = 'DrhContentSlider';
  var ROOT_SELECTOR = '[data-content-slider]';
  var SLIDE_SELECTOR = ':scope > [data-slide]';
  var TRANSITION_DURATION = 400;
  var DRAG_THRESHOLD_MULTI = 30;
  var DRAG_THRESHOLD_SINGLE = 50;
  var WHEEL_THRESHOLD = 5;
  var WHEEL_SNAP_DELAY = 120;
  var RESIZE_DEBOUNCE = 100;

  function rootsInScope(scope) {
    if (!scope) return [];
    var roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(ROOT_SELECTOR).forEach(function(root) {
        roots.push(root);
      });
    }
    return roots;
  }

  function addListener(cleanups, target, type, listener, options) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(type, listener, options);
    cleanups.push(function() {
      target.removeEventListener(type, listener, options);
    });
  }

  function parseInteger(value, fallback) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function formatTemplate(template, replacements, fallback) {
    var result = template || fallback || '';
    Object.keys(replacements).forEach(function(key) {
      result = result.split('__' + key.toUpperCase() + '__').join(String(replacements[key]));
    });
    return result;
  }

  class ContentSliderController {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-track]');
      this.previousButton = root.querySelector('[data-slider-prev]');
      this.nextButton = root.querySelector('[data-slider-next]');
      this.dotsContainer = root.querySelector('[data-slider-dots]');
      this.autoplayButton = root.querySelector('[data-slider-autoplay]');
      this.liveRegion = root.querySelector('[data-slider-status]');

      this.totalSlides = parseInteger(root.dataset.totalSlides, 0);
      this.transition = root.dataset.transition || 'slide';
      this.loop = root.dataset.loop === 'true';
      this.needsSliderDesktop = root.dataset.needsSliderDesktop === 'true';
      this.needsSliderMobile = root.dataset.needsSliderMobile === 'true';
      this.scrollCountDesktop = parseInteger(root.dataset.scrollCountDesktop, 1);
      this.scrollCountMobile = parseInteger(root.dataset.scrollCountMobile, 1);
      this.autoslideDesktop = root.dataset.autoslideDesktop === 'true';
      this.autoslideMobile = root.dataset.autoslideMobile === 'true';
      this.autoslideInterval = parseInteger(root.dataset.autoslideInterval, 5000);
      this.autoslideCountDesktop = parseInteger(root.dataset.autoslideCountDesktop, 1);
      this.autoslideCountMobile = parseInteger(root.dataset.autoslideCountMobile, 1);
      this.isSingleView = root.classList.contains('content-slider--single');

      this.breakpointQuery = window.matchMedia('(min-width: 768px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isDesktop = this.breakpointQuery.matches;
      this.motionReduced = this.motionQuery.matches;
      this.motionOverride = false;
      this.autoplayPointerWasRunning = null;
      this.focusedControlRegion = '';
      this.needsSlider = false;
      this.isAutoslide = false;
      this.isAnimating = false;
      this.autoslideStopped = false;
      this.isPointerInside = false;
      this.isIntersecting = true;
      this.destroyed = false;

      this.currentIndex = 0;
      this.scrollIndex = 0;
      this.scrollCount = 1;
      this.autoslideCount = 1;
      this.slidesPerView = 1;
      this.maxScrollIndex = 0;
      this.slideUnit = 0;

      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerDelta = 0;
      this.pointerAxis = '';
      this.isDragging = false;
      this.wheelAccum = 0;
      this.pendingTransform = '';

      this.autoslideTimer = null;
      this.resizeTimer = null;
      this.focusTimer = null;
      this.wheelTimer = null;
      this.transitionTimer = null;
      this.transformFrame = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.cleanups = [];
      this.temporaryRootTabindex = false;

      if (!this.track || this.totalSlides < 2) return;

      this.ready = true;
      this.bindEvents();
      this.bindObservers();
      if (this.isSingleView) this.initSingleView();
      else this.updateMultiLayout();
      this.startAutoslide();
    }

    slides() {
      return Array.prototype.slice.call(this.track.querySelectorAll(SLIDE_SELECTOR));
    }

    slideAt(index) {
      return this.track.querySelector(':scope > [data-slide="' + index + '"]');
    }

    text(name, fallback) {
      return this.root.dataset[name] || fallback || '';
    }

    animationAllowed() {
      return !this.motionReduced;
    }

    bindEvents() {
      var self = this;

      addListener(this.cleanups, this.root, 'click', function(event) {
        var target = event.target && event.target.closest ? event.target : null;
        if (!target || !self.root.contains(target)) return;

        if (self.autoplayButton && target.closest('[data-slider-autoplay]') === self.autoplayButton) {
          self.toggleAutoslide(self.autoplayPointerWasRunning === true);
          self.autoplayPointerWasRunning = null;
          return;
        }
        if (self.previousButton && target.closest('[data-slider-prev]') === self.previousButton) {
          self.pauseAutoslideByUser();
          self.goPrevious();
          return;
        }
        if (self.nextButton && target.closest('[data-slider-next]') === self.nextButton) {
          self.pauseAutoslideByUser();
          self.goNext();
          return;
        }

        var dot = target.closest('.content-slider__dot');
        if (!dot || !self.dotsContainer || !self.dotsContainer.contains(dot)) return;
        self.pauseAutoslideByUser();
        var targetIndex = parseNumber(dot.dataset.slide, 0);
        if (self.isSingleView) self.goToSlide(targetIndex);
        else self.scrollTo(targetIndex);
      });
      addListener(this.cleanups, this.root, 'pointerdown', function(event) {
        var target = event.target && event.target.closest ? event.target : null;
        if (self.autoplayButton && target && target.closest('[data-slider-autoplay]') === self.autoplayButton) {
          self.autoplayPointerWasRunning = self.userRotationEnabled();
        }
      }, true);

      addListener(this.cleanups, this.track, 'pointerdown', function(event) {
        self.handlePointerDown(event);
      });
      addListener(this.cleanups, this.track, 'pointermove', function(event) {
        self.handlePointerMove(event);
      });
      addListener(this.cleanups, this.track, 'pointerup', function(event) {
        self.handlePointerEnd(event, false);
      });
      addListener(this.cleanups, this.track, 'pointercancel', function(event) {
        self.handlePointerEnd(event, true);
      });
      addListener(this.cleanups, this.track, 'dragstart', function(event) {
        if (self.ownsTrackGesture(event.target)) event.preventDefault();
      });
      addListener(this.cleanups, this.track, 'wheel', function(event) {
        self.handleWheel(event);
      }, { passive: false });

      addListener(this.cleanups, this.root, 'mouseenter', function() {
        self.isPointerInside = true;
        self.stopAutoslide();
      });
      addListener(this.cleanups, this.root, 'mouseleave', function() {
        self.isPointerInside = false;
        self.startAutoslide();
      });
      addListener(this.cleanups, this.root, 'focusin', function(event) {
        var navigation = self.root.querySelector('.content-slider__nav');
        if (self.autoplayButton && self.autoplayButton.contains(event.target)) {
          self.focusedControlRegion = 'autoplay';
        } else if (navigation && navigation.contains(event.target)) {
          self.focusedControlRegion = 'navigation';
        } else {
          self.focusedControlRegion = '';
        }
        self.pauseAutoslideByUser();
      });
      addListener(this.cleanups, this.root, 'focusout', function() {
        window.clearTimeout(self.focusTimer);
        self.focusTimer = window.setTimeout(function() {
          if (self.destroyed || self.root.contains(document.activeElement)) return;
          var desktop = self.breakpointQuery.matches;
          var needsSlider = desktop ? self.needsSliderDesktop : self.needsSliderMobile;
          var hasAutoslide = desktop ? self.autoslideDesktop : self.autoslideMobile;
          var hiddenControlLostFocus = (self.focusedControlRegion === 'navigation' && !needsSlider)
            || (self.focusedControlRegion === 'autoplay' && (!needsSlider || !hasAutoslide));
          if (hiddenControlLostFocus) {
            self.focusRoot();
            return;
          }
          self.focusedControlRegion = '';
          self.startAutoslide();
        }, 0);
      });
      addListener(this.cleanups, document, 'visibilitychange', function() {
        if (document.hidden) self.stopAutoslide();
        else self.startAutoslide();
      });
      addListener(this.cleanups, this.breakpointQuery, 'change', function() {
        self.handleResponsiveChange();
      });
      addListener(this.cleanups, this.motionQuery, 'change', function(event) {
        self.motionReduced = event.matches;
        if (self.motionReduced) {
          self.motionOverride = false;
          self.stopAutoslide();
          self.cancelAnimation();
          if (self.isSingleView) {
            self.syncSingleAccessibility();
            self.updateDots();
            self.updateArrows();
          } else {
            self.applyMultiTransform(false);
            self.syncMultiAccessibility();
          }
        }
        self.syncAutoplayControl();
        self.startAutoslide();
      });
      addListener(this.cleanups, window, 'resize', function() {
        self.scheduleResize();
      });
    }

    bindObservers() {
      var self = this;
      if ('ResizeObserver' in window) {
        this.resizeObserver = new window.ResizeObserver(function() {
          self.scheduleResize();
        });
        this.resizeObserver.observe(this.root);
      }
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new window.IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.target !== self.root) return;
            self.isIntersecting = entry.isIntersecting;
            if (entry.isIntersecting) self.startAutoslide();
            else self.stopAutoslide();
          });
        }, { threshold: 0.2 });
        this.intersectionObserver.observe(this.root);
      }
    }

    scheduleResize() {
      var self = this;
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(function() {
        if (self.destroyed) return;
        // Revealing a differently sized single-view slide can resize the root
        // itself. Preserve the in-flight destination and perform the layout
        // refresh after the transition has committed.
        if (self.isSingleView && self.isAnimating) {
          self.scheduleResize();
          return;
        }
        self.handleResponsiveChange();
      }, RESIZE_DEBOUNCE);
    }

    updateViewportConfig() {
      this.isDesktop = this.breakpointQuery.matches;
      this.needsSlider = this.isDesktop ? this.needsSliderDesktop : this.needsSliderMobile;
      this.isAutoslide = this.isDesktop ? this.autoslideDesktop : this.autoslideMobile;
      this.scrollCount = this.isDesktop ? this.scrollCountDesktop : this.scrollCountMobile;
      this.autoslideCount = this.isDesktop ? this.autoslideCountDesktop : this.autoslideCountMobile;
    }

    handleResponsiveChange() {
      var wasDesktop = this.isDesktop;
      var navigation = this.root.querySelector('.content-slider__nav');
      var focusedNavigation = (navigation && navigation.contains(document.activeElement))
        || this.focusedControlRegion === 'navigation';
      var focusedAutoplay = (this.autoplayButton && this.autoplayButton.contains(document.activeElement))
        || this.focusedControlRegion === 'autoplay';
      this.cancelAnimation();
      this.updateViewportConfig();
      if ((!this.needsSlider && focusedNavigation) || (!this.isAutoslide && focusedAutoplay)) {
        this.focusRoot();
      }
      if (this.isSingleView) {
        this.syncSingleAccessibility();
        this.updateDots();
        this.updateArrows();
      } else {
        if (wasDesktop !== this.isDesktop) this.scrollIndex = 0;
        this.updateMultiLayout();
      }
      this.syncAutoplayControl();
      this.startAutoslide();
    }

    initSingleView() {
      this.updateViewportConfig();
      var activeSlide = this.track.querySelector(':scope > [data-slide].is-active');
      this.currentIndex = activeSlide ? parseInteger(activeSlide.dataset.slide, 0) : 0;
      this.ensureSlideInDom(this.currentIndex);
      this.syncSingleAccessibility();
      this.updateDots();
      this.updateArrows();
      this.syncAutoplayControl();
    }

    updateMultiLayout() {
      this.updateViewportConfig();
      var style = window.getComputedStyle(this.root);
      var desktop = parseFloat(style.getPropertyValue('--slides-per-view-desktop')) || 1;
      var mobile = parseFloat(style.getPropertyValue('--slides-per-view-mobile')) || 1;
      this.slidesPerView = this.isDesktop ? desktop : mobile;
      this.maxScrollIndex = this.needsSlider
        ? Math.max(0, this.totalSlides - this.slidesPerView)
        : 0;
      this.scrollIndex = Math.max(0, Math.min(this.scrollIndex, this.maxScrollIndex));
      this.measureGeometry();
      this.applyMultiTransform(false);
      this.rebuildDots();
      this.updateDots();
      this.updateArrows();
      this.syncMultiAccessibility();
      this.syncAutoplayControl();
    }

    measureGeometry() {
      var firstSlide = this.slideAt(0) || this.slides()[0];
      if (!firstSlide) {
        this.slideUnit = 0;
        return;
      }
      var trackStyle = window.getComputedStyle(this.track);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      this.slideUnit = firstSlide.offsetWidth + gap;
    }

    ownsTrackGesture(target) {
      if (!target || !target.closest) return true;
      var gestureTrack = target.closest('[data-slider-track], [data-slider-inline-track]');
      return !gestureTrack || gestureTrack === this.track;
    }

    applyMultiTransform(animate) {
      this.cancelPendingTransform();
      window.clearTimeout(this.transitionTimer);
      var shouldAnimate = Boolean(animate && this.animationAllowed());
      this.track.classList.toggle('content-slider__track--animate', shouldAnimate);
      if (!this.needsSlider) {
        this.track.style.transform = '';
        return;
      }
      if (!this.slideUnit) this.measureGeometry();
      this.track.style.transform = 'translateX(-' + (this.scrollIndex * this.slideUnit) + 'px)';
      if (shouldAnimate) {
        var self = this;
        this.transitionTimer = window.setTimeout(function() {
          self.track.classList.remove('content-slider__track--animate');
          self.isAnimating = false;
        }, TRANSITION_DURATION);
      } else {
        this.isAnimating = false;
      }
    }

    scrollTo(index, options) {
      if (this.destroyed || !this.needsSlider) return;
      var settings = options || {};
      var target = Math.max(0, Math.min(Number(index) || 0, this.maxScrollIndex));
      if (target === this.scrollIndex && !settings.force) return;
      this.scrollIndex = target;
      this.isAnimating = settings.animate !== false && this.animationAllowed();
      this.applyMultiTransform(settings.animate !== false);
      this.updateDots();
      this.updateArrows();
      this.syncMultiAccessibility();
      if (settings.announce !== false) this.announce();
    }

    nextMultiTarget(step) {
      var positions = this.multiPositions(step);
      for (var i = 0; i < positions.length; i++) {
        if (positions[i] > this.scrollIndex + 0.001) return positions[i];
      }
      return this.loop ? positions[0] : positions[positions.length - 1];
    }

    previousMultiTarget(step) {
      var positions = this.multiPositions(step);
      for (var i = positions.length - 1; i >= 0; i--) {
        if (positions[i] < this.scrollIndex - 0.001) return positions[i];
      }
      return this.loop ? positions[positions.length - 1] : positions[0];
    }

    multiPositions(step) {
      var increment = Math.max(1, Number(step) || 1);
      var positions = [0];
      var next = increment;
      while (next < this.maxScrollIndex - 0.001) {
        positions.push(next);
        next += increment;
      }
      if (this.maxScrollIndex > 0 && Math.abs(positions[positions.length - 1] - this.maxScrollIndex) > 0.001) {
        positions.push(this.maxScrollIndex);
      }
      return positions;
    }

    goNext(options) {
      if (this.isAnimating || !this.needsSlider) return;
      if (this.isSingleView) {
        var next = this.currentIndex + 1;
        if (next >= this.totalSlides) next = this.loop ? 0 : this.currentIndex;
        this.goToSlide(next, Object.assign({}, options, { direction: -1 }));
        return;
      }
      this.scrollTo(this.nextMultiTarget(this.scrollCount), options);
    }

    goPrevious(options) {
      if (this.isAnimating || !this.needsSlider) return;
      if (this.isSingleView) {
        var previous = this.currentIndex - 1;
        if (previous < 0) previous = this.loop ? this.totalSlides - 1 : this.currentIndex;
        this.goToSlide(previous, Object.assign({}, options, { direction: 1 }));
        return;
      }
      this.scrollTo(this.previousMultiTarget(this.scrollCount), options);
    }

    updateArrows() {
      if (!this.previousButton || !this.nextButton) return;
      var current = this.isSingleView ? this.currentIndex : this.scrollIndex;
      var maximum = this.isSingleView ? this.totalSlides - 1 : this.maxScrollIndex;
      var unavailable = !this.needsSlider;
      this.previousButton.disabled = unavailable || (!this.loop && current <= 0);
      this.nextButton.disabled = unavailable || (!this.loop && current >= maximum);
    }

    activeDot() {
      if (!this.dotsContainer) return null;
      var dots = Array.prototype.slice.call(this.dotsContainer.querySelectorAll('.content-slider__dot'));
      if (!dots.length) return null;
      var current = this.isSingleView ? this.currentIndex : this.scrollIndex;
      return dots.reduce(function(closest, dot) {
        if (!closest) return dot;
        return Math.abs(parseNumber(dot.dataset.slide, 0) - current)
          < Math.abs(parseNumber(closest.dataset.slide, 0) - current) ? dot : closest;
      }, null);
    }

    updateDots() {
      if (!this.dotsContainer) return;
      var active = this.activeDot();
      this.dotsContainer.querySelectorAll('.content-slider__dot').forEach(function(dot) {
        var isActive = dot === active;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    rebuildDots() {
      if (!this.dotsContainer || this.isSingleView) return;
      var targets = this.needsSlider ? this.multiPositions(this.scrollCount) : [0];
      var existing = Array.prototype.slice.call(this.dotsContainer.querySelectorAll('.content-slider__dot'));
      var currentTargets = existing.map(function(dot) { return parseNumber(dot.dataset.slide, 0); });
      if (currentTargets.join(',') === targets.join(',')) return;

      var hadFocus = this.dotsContainer.contains(document.activeElement);
      var fragment = document.createDocumentFragment();
      var self = this;
      targets.forEach(function(target, index) {
        var first = Math.floor(target) + 1;
        var last = Math.min(self.totalSlides, Math.ceil(target + self.slidesPerView));
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'content-slider__dot';
        dot.dataset.slide = String(target);
        dot.setAttribute('aria-controls', self.track.id);
        dot.setAttribute('aria-label', formatTemplate(
          self.text('groupButtonLabelTemplate'),
          {
            position: index + 1,
            positions: targets.length,
            first: first,
            last: last,
            total: self.totalSlides
          },
          'Show position ' + (index + 1) + ' of ' + targets.length
        ));
        if (index === 0) {
          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');
        }
        fragment.appendChild(dot);
      });
      this.dotsContainer.replaceChildren(fragment);
      this.updateDots();
      if (hadFocus) this.focusElement(this.activeDot());
    }

    visibleMultiSlides() {
      if (!this.needsSlider) return this.slides();
      var start = Math.floor(this.scrollIndex);
      var end = Math.min(this.totalSlides, Math.ceil(this.scrollIndex + this.slidesPerView));
      return this.slides().filter(function(slide) {
        var index = parseInteger(slide.dataset.slide, -1);
        return index >= start && index < end;
      });
    }

    syncMultiAccessibility() {
      var slides = this.slides();
      var visible = new Set(this.visibleMultiSlides());
      var hidden = slides.filter(function(slide) { return !visible.has(slide); });
      visible.forEach(function(slide) {
        slide.removeAttribute('inert');
        slide.setAttribute('aria-hidden', 'false');
      });
      this.rescueFocus(hidden);
      hidden.forEach(function(slide) {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      });
    }

    ensureSlideInDom(index) {
      if (index < 0 || index >= this.totalSlides) return null;
      var existing = this.slideAt(index);
      if (existing) return existing;
      var template = this.track.querySelector('template[data-slide-template="' + index + '"]');
      if (!template) return null;
      var content = template.content.cloneNode(true);
      var inserted = false;
      Array.prototype.slice.call(this.track.children).some(function(child) {
        var childIndex = child.tagName === 'TEMPLATE'
          ? parseInteger(child.dataset.slideTemplate, -1)
          : parseInteger(child.dataset.slide, -1);
        if (childIndex <= index) return false;
        this.track.insertBefore(content, child);
        inserted = true;
        return true;
      }, this);
      if (!inserted) this.track.appendChild(content);
      template.remove();
      return this.slideAt(index);
    }

    moveSlideToTemplate(index) {
      var slide = this.slideAt(index);
      if (!slide) return;
      this.rescueFocus([slide]);
      var template = document.createElement('template');
      template.dataset.slideTemplate = String(index);
      template.content.appendChild(slide);
      var inserted = false;
      Array.prototype.slice.call(this.track.children).some(function(child) {
        var childIndex = child.tagName === 'TEMPLATE'
          ? parseInteger(child.dataset.slideTemplate, -1)
          : parseInteger(child.dataset.slide, -1);
        if (childIndex <= index) return false;
        this.track.insertBefore(template, child);
        inserted = true;
        return true;
      }, this);
      if (!inserted) this.track.appendChild(template);
    }

    cleanupDistantSlides() {
      var current = this.currentIndex;
      var total = this.totalSlides;
      var keep = new Set([current, (current - 1 + total) % total, (current + 1) % total]);
      var self = this;
      this.slides().forEach(function(slide) {
        var index = parseInteger(slide.dataset.slide, -1);
        if (!keep.has(index)) self.moveSlideToTemplate(index);
      });
    }

    syncSingleAccessibility() {
      var target = this.ensureSlideInDom(this.currentIndex);
      if (!target) return;
      var hidden = this.slides().filter(function(slide) { return slide !== target; });
      target.hidden = false;
      target.classList.add('is-active');
      target.removeAttribute('inert');
      target.setAttribute('aria-hidden', 'false');
      this.rescueFocus(hidden);
      hidden.forEach(function(slide) {
        slide.classList.remove('is-active');
        slide.hidden = true;
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      });
      this.cleanupDistantSlides();
    }

    prepareSingleTransition(fromSlide, toSlide) {
      toSlide.hidden = false;
      toSlide.classList.add('is-active');
      toSlide.removeAttribute('inert');
      toSlide.setAttribute('aria-hidden', 'false');
      this.rescueFocus([fromSlide], parseInteger(toSlide.dataset.slide, this.currentIndex));
      fromSlide.setAttribute('aria-hidden', 'true');
      fromSlide.setAttribute('inert', '');
    }

    goToSlide(index, options) {
      if (this.destroyed || this.isAnimating || !this.needsSlider) return;
      var settings = options || {};
      var targetIndex = Math.max(0, Math.min(parseInteger(index, this.currentIndex), this.totalSlides - 1));
      if (targetIndex === this.currentIndex && !settings.force) return;

      var fromIndex = this.currentIndex;
      var fromSlide = this.ensureSlideInDom(fromIndex);
      var toSlide = this.ensureSlideInDom(targetIndex);
      this.ensureSlideInDom((targetIndex - 1 + this.totalSlides) % this.totalSlides);
      this.ensureSlideInDom((targetIndex + 1) % this.totalSlides);
      if (!fromSlide || !toSlide) return;

      if (settings.animate === false || !this.animationAllowed()) {
        this.finishSingleNavigation(fromIndex, targetIndex, settings);
        return;
      }

      this.isAnimating = true;
      this.prepareSingleTransition(fromSlide, toSlide);
      if (this.transition === 'fade') {
        fromSlide.classList.remove('is-active');
        this.completeSingleAfterTransition(fromIndex, targetIndex, settings);
        return;
      }

      var forwardWrap = this.loop && this.totalSlides > 2 && fromIndex === this.totalSlides - 1 && targetIndex === 0;
      var backwardWrap = this.loop && this.totalSlides > 2 && fromIndex === 0 && targetIndex === this.totalSlides - 1;
      var direction = settings.direction || (forwardWrap ? -1 : (backwardWrap ? 1 : (targetIndex > fromIndex ? -1 : 1)));
      toSlide.style.position = 'absolute';
      toSlide.style.top = '0';
      toSlide.style.left = direction === -1 ? '100%' : '-100%';
      toSlide.style.width = '100%';
      this.track.classList.add('content-slider__track--animate');
      this.track.style.transform = 'translateX(' + (direction * 100) + '%)';
      this.completeSingleAfterTransition(fromIndex, targetIndex, settings);
    }

    completeSingleAfterTransition(fromIndex, toIndex, settings) {
      var self = this;
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = window.setTimeout(function() {
        self.finishSingleNavigation(fromIndex, toIndex, settings);
      }, TRANSITION_DURATION);
    }

    finishSingleNavigation(fromIndex, toIndex, options) {
      var settings = options || {};
      var fromSlide = this.slideAt(fromIndex);
      var toSlide = this.ensureSlideInDom(toIndex);
      if (!toSlide) return;
      if (fromSlide && fromSlide !== toSlide) {
        this.rescueFocus([fromSlide], toIndex);
        fromSlide.classList.remove('is-active');
        fromSlide.hidden = true;
        fromSlide.setAttribute('aria-hidden', 'true');
        fromSlide.setAttribute('inert', '');
      }
      this.slides().forEach(function(slide) {
        slide.style.position = '';
        slide.style.top = '';
        slide.style.left = '';
        slide.style.width = '';
      });
      this.track.style.transform = '';
      this.track.classList.remove('content-slider__track--animate');
      this.currentIndex = toIndex;
      this.isAnimating = false;
      this.syncSingleAccessibility();
      this.updateDots();
      this.updateArrows();
      if (settings.announce !== false) this.announce();
    }

    focusElement(element) {
      if (!element || element.hidden || element.disabled || typeof element.focus !== 'function') return false;
      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
      return document.activeElement === element;
    }

    focusFallback(preferredIndex) {
      var activeDot = null;
      if (this.dotsContainer && Number.isFinite(preferredIndex)) {
        activeDot = this.dotsContainer.querySelector('.content-slider__dot[data-slide="' + preferredIndex + '"]');
      }
      if (!activeDot) activeDot = this.activeDot();
      if (this.focusElement(activeDot)) return;
      if (this.focusElement(this.autoplayButton)) return;
      if (this.focusElement(this.nextButton)) return;
      if (this.focusElement(this.previousButton)) return;
      this.focusRoot();
    }

    focusRoot() {
      if (!this.root.hasAttribute('tabindex')) {
        this.root.setAttribute('tabindex', '-1');
        this.temporaryRootTabindex = true;
      }
      this.focusElement(this.root);
    }

    rescueFocus(slides, preferredIndex) {
      var activeElement = document.activeElement;
      if (!activeElement) return;
      var needsRescue = slides.some(function(slide) {
        return slide === activeElement || slide.contains(activeElement);
      });
      if (needsRescue) this.focusFallback(preferredIndex);
    }

    queueTransform(value) {
      this.pendingTransform = value;
      if (this.transformFrame !== null) return;
      var self = this;
      var requestFrame = window.requestAnimationFrame || function(callback) {
        return window.setTimeout(callback, 16);
      };
      this.transformFrame = requestFrame(function() {
        self.transformFrame = null;
        if (!self.destroyed) self.track.style.transform = self.pendingTransform;
      });
    }

    cancelPendingTransform() {
      if (this.transformFrame === null) return;
      var cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
      cancelFrame(this.transformFrame);
      this.transformFrame = null;
      this.pendingTransform = '';
    }

    previewSingleGesture(delta) {
      if (this.transition !== 'slide') return;
      var width = this.track.getBoundingClientRect().width;
      var index = this.currentIndex + (delta < 0 ? 1 : -1);
      if (this.loop) index = (index + this.totalSlides) % this.totalSlides;
      var neighbor = index >= 0 && index < this.totalSlides ? this.ensureSlideInDom(index) : null;
      this.slides().forEach(function(slide) {
        if (parseInteger(slide.dataset.slide, -1) === this.currentIndex) return;
        slide.hidden = slide !== neighbor;
        slide.style.position = slide === neighbor ? 'absolute' : '';
        slide.style.top = slide === neighbor ? '0' : '';
        slide.style.left = slide === neighbor ? (delta < 0 ? '100%' : '-100%') : '';
        slide.style.width = slide === neighbor ? '100%' : '';
      }, this);
      // Preview the neighbor visually while it stays inert and aria-hidden.
      var offset = neighbor ? delta : delta * 0.25;
      if (width > 0) offset = Math.max(-width, Math.min(width, offset));
      this.queueTransform('translateX(' + offset + 'px)');
    }

    settleSingleGesture(cancelled) {
      this.cancelPendingTransform();
      var index = this.currentIndex;
      if (cancelled || !this.animationAllowed() || this.transition !== 'slide') {
        this.finishSingleNavigation(index, index, { announce: false });
        return;
      }
      this.isAnimating = true;
      this.track.classList.add('content-slider__track--animate');
      this.track.style.transform = 'translateX(0px)';
      this.completeSingleAfterTransition(index, index, { announce: false });
    }

    handlePointerDown(event) {
      if (!this.ownsTrackGesture(event.target) || !this.needsSlider || this.isAnimating || event.button !== 0) return;
      if (event.target.closest && event.target.closest('a, button, input, select, textarea')) return;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerDelta = 0;
      this.pointerAxis = '';
      this.isDragging = true;
      this.measureGeometry();
      this.stopAutoslide();
      if (this.track.setPointerCapture) this.track.setPointerCapture(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;
      var deltaX = event.clientX - this.pointerStartX;
      var deltaY = event.clientY - this.pointerStartY;
      if (!this.pointerAxis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8) {
        this.pointerAxis = Math.abs(deltaY) > Math.abs(deltaX) ? 'vertical' : 'horizontal';
        if (this.pointerAxis === 'horizontal') this.root.classList.add('content-slider--grabbing');
      }
      if (this.pointerAxis !== 'horizontal') return;
      event.preventDefault();
      this.pointerDelta = deltaX;
      if (this.isSingleView) {
        this.previewSingleGesture(deltaX);
      } else {
        this.queueTransform('translateX(' + (-(this.scrollIndex * this.slideUnit) + deltaX) + 'px)');
      }
    }

    handlePointerEnd(event, cancelled) {
      if (!this.isDragging || event.pointerId !== this.pointerId) return;
      if (this.track.releasePointerCapture && this.track.hasPointerCapture && this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
      var delta = this.pointerDelta;
      var horizontal = this.pointerAxis === 'horizontal';
      this.isDragging = false;
      this.pointerId = null;
      this.pointerDelta = 0;
      this.pointerAxis = '';
      this.root.classList.remove('content-slider--grabbing');
      this.cancelPendingTransform();
      if (cancelled || !horizontal) {
        if (this.isSingleView) this.settleSingleGesture(true);
        else this.applyMultiTransform(false);
        this.startAutoslide();
        return;
      }
      var threshold = this.isSingleView ? DRAG_THRESHOLD_SINGLE : DRAG_THRESHOLD_MULTI;
      if (Math.abs(delta) <= threshold) {
        if (this.isSingleView) this.settleSingleGesture(false);
        else this.applyMultiTransform(true);
        this.startAutoslide();
        return;
      }
      this.pauseAutoslideByUser();
      if (this.isSingleView) {
        var canNavigate = this.loop || (delta < 0 ? this.currentIndex < this.totalSlides - 1 : this.currentIndex > 0);
        if (!canNavigate) this.settleSingleGesture(false);
        else if (delta < 0) this.goNext();
        else this.goPrevious();
        return;
      }
      var slidesMoved = this.slideUnit > 0 ? Math.max(1, Math.round(Math.abs(delta) / this.slideUnit)) : 1;
      var target = delta < 0
        ? this.nextMultiTarget(slidesMoved)
        : this.previousMultiTarget(slidesMoved);
      this.scrollTo(target);
    }

    handleWheel(event) {
      if (!this.ownsTrackGesture(event.target) || !this.needsSlider || this.isAnimating) return;
      var horizontal = Math.abs(event.deltaX);
      if (horizontal <= WHEEL_THRESHOLD || horizontal <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      this.stopAutoslide();
      if (!this.slideUnit) this.measureGeometry();
      this.wheelAccum += event.deltaX;
      if (this.isSingleView) {
        this.previewSingleGesture(-this.wheelAccum);
      } else {
        this.queueTransform('translateX(' + (-(this.scrollIndex * this.slideUnit) - this.wheelAccum) + 'px)');
      }
      var self = this;
      window.clearTimeout(this.wheelTimer);
      this.wheelTimer = window.setTimeout(function() {
        self.finishWheel();
      }, WHEEL_SNAP_DELAY);
    }

    finishWheel() {
      this.cancelPendingTransform();
      var accumulated = this.wheelAccum;
      this.wheelAccum = 0;
      var threshold = this.isSingleView ? DRAG_THRESHOLD_SINGLE : DRAG_THRESHOLD_MULTI;
      if (Math.abs(accumulated) <= threshold) {
        if (this.isSingleView) this.settleSingleGesture(false);
        else this.applyMultiTransform(true);
        this.startAutoslide();
        return;
      }
      this.pauseAutoslideByUser();
      if (this.isSingleView) {
        var canNavigate = this.loop || (accumulated > 0 ? this.currentIndex < this.totalSlides - 1 : this.currentIndex > 0);
        if (!canNavigate) this.settleSingleGesture(false);
        else if (accumulated > 0) this.goNext();
        else this.goPrevious();
        return;
      }
      var slidesMoved = this.slideUnit > 0 ? Math.max(1, Math.round(Math.abs(accumulated) / this.slideUnit)) : 1;
      var target = accumulated > 0
        ? this.nextMultiTarget(slidesMoved)
        : this.previousMultiTarget(slidesMoved);
      this.scrollTo(target);
    }

    userRotationEnabled() {
      return !this.autoslideStopped && (!this.motionReduced || this.motionOverride);
    }

    shouldAutoslide() {
      return !this.destroyed
        && this.needsSlider
        && this.isAutoslide
        && this.userRotationEnabled()
        && this.isIntersecting
        && !document.hidden
        && !this.isPointerInside
        && !this.root.contains(document.activeElement);
    }

    startAutoslide() {
      this.stopAutoslide();
      this.syncAutoplayControl();
      if (!this.shouldAutoslide()) return;
      var self = this;
      this.autoslideTimer = window.setInterval(function() {
        if (!self.shouldAutoslide()) {
          self.stopAutoslide();
          return;
        }
        if (self.isSingleView) {
          if (!self.loop && self.currentIndex >= self.totalSlides - 1) {
            self.autoslideStopped = true;
            self.stopAutoslide();
            self.syncAutoplayControl();
            return;
          }
          var next = self.currentIndex + 1;
          if (next >= self.totalSlides) next = 0;
          self.goToSlide(next, { announce: false, direction: -1 });
          return;
        }
        var target = self.nextMultiTarget(self.autoslideCount);
        if (!self.loop && target === self.scrollIndex) {
          self.autoslideStopped = true;
          self.stopAutoslide();
          self.syncAutoplayControl();
          return;
        }
        self.scrollTo(target, { announce: false });
      }, this.autoslideInterval);
    }

    stopAutoslide() {
      if (this.autoslideTimer !== null) {
        window.clearInterval(this.autoslideTimer);
        this.autoslideTimer = null;
      }
    }

    pauseAutoslideByUser() {
      this.autoslideStopped = true;
      this.motionOverride = false;
      this.stopAutoslide();
      this.syncAutoplayControl();
    }

    toggleAutoslide(forcePause) {
      if (forcePause) {
        this.pauseAutoslideByUser();
        return;
      }
      if (this.userRotationEnabled()) {
        this.pauseAutoslideByUser();
        return;
      }
      this.autoslideStopped = false;
      this.motionOverride = true;
      if (!this.loop) {
        if (this.isSingleView && this.currentIndex >= this.totalSlides - 1) {
          this.goToSlide(0, { animate: false, announce: false, force: true });
        } else if (!this.isSingleView && this.scrollIndex >= this.maxScrollIndex) {
          this.scrollTo(0, { animate: false, announce: false, force: true });
        }
      }
      this.syncAutoplayControl();
      this.startAutoslide();
    }

    syncAutoplayControl() {
      if (!this.autoplayButton) return;
      var available = this.isAutoslide && this.needsSlider;
      var runningIntent = available && this.userRotationEnabled();
      this.autoplayButton.hidden = !available;
      this.autoplayButton.dataset.state = runningIntent ? 'running' : 'paused';
      this.autoplayButton.setAttribute('aria-label', runningIntent
        ? this.text('pauseLabel', 'Pause automatic slide rotation')
        : this.text('playLabel', 'Start automatic slide rotation'));
      var pauseIcon = this.autoplayButton.querySelector('[data-autoplay-pause-icon]');
      var playIcon = this.autoplayButton.querySelector('[data-autoplay-play-icon]');
      if (pauseIcon) pauseIcon.hidden = !runningIntent;
      if (playIcon) playIcon.hidden = runningIntent;
      if (this.liveRegion) this.liveRegion.setAttribute('aria-live', runningIntent ? 'off' : 'polite');
    }

    announce() {
      if (!this.liveRegion) return;
      if (this.isSingleView) {
        this.liveRegion.textContent = formatTemplate(
          this.text('slideStatusTemplate'),
          { current: this.currentIndex + 1, total: this.totalSlides },
          'Slide ' + (this.currentIndex + 1) + ' of ' + this.totalSlides
        );
        return;
      }
      var last = Math.min(this.totalSlides, Math.ceil(this.scrollIndex + this.slidesPerView));
      var positions = this.multiPositions(this.scrollCount);
      var currentPosition = positions.reduce(function(closestIndex, position, index) {
        return Math.abs(position - this.scrollIndex) < Math.abs(positions[closestIndex] - this.scrollIndex)
          ? index
          : closestIndex;
      }.bind(this), 0);
      this.liveRegion.textContent = formatTemplate(
        this.text('groupStatusTemplate'),
        {
          position: currentPosition + 1,
          positions: positions.length,
          first: Math.floor(this.scrollIndex) + 1,
          last: last,
          total: this.totalSlides
        },
        'Position ' + (currentPosition + 1) + ' of ' + positions.length
      );
    }

    cancelAnimation() {
      this.cancelPendingTransform();
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
      this.isAnimating = false;
      this.track.classList.remove('content-slider__track--animate');
      this.track.style.transform = '';
      this.slides().forEach(function(slide) {
        slide.style.position = '';
        slide.style.top = '';
        slide.style.left = '';
        slide.style.width = '';
      });
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.stopAutoslide();
      window.clearTimeout(this.resizeTimer);
      window.clearTimeout(this.focusTimer);
      window.clearTimeout(this.wheelTimer);
      window.clearTimeout(this.transitionTimer);
      this.cancelPendingTransform();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      this.cleanups.splice(0).forEach(function(cleanup) { cleanup(); });
      this.cancelAnimation();
      this.root.classList.remove('content-slider--grabbing');
      if (this.isSingleView) {
        this.currentIndex = 0;
        this.ensureSlideInDom(0);
        this.syncSingleAccessibility();
      } else {
        this.slides().forEach(function(slide) {
          slide.removeAttribute('inert');
          slide.removeAttribute('aria-hidden');
        });
      }
      if (this.temporaryRootTabindex) this.root.removeAttribute('tabindex');
      delete this.root.__drhContentSliderController;
      delete this.root.dataset.contentSliderInitialized;
    }
  }

  function createController(root) {
    if (root.__drhContentSliderController) return root.__drhContentSliderController;
    var controller = new ContentSliderController(root);
    if (!controller.ready) return null;
    root.__drhContentSliderController = controller;
    root.dataset.contentSliderInitialized = 'true';
    return controller;
  }

  function initScope(scope) {
    rootsInScope(scope || document).forEach(function(root) {
      createController(root);
    });
  }

  function destroyScope(scope) {
    rootsInScope(scope || document).forEach(function(root) {
      if (root.__drhContentSliderController) root.__drhContentSliderController.destroy();
    });
  }

  if (window[MANAGER_KEY] && typeof window[MANAGER_KEY].initScope === 'function') {
    window[MANAGER_KEY].initScope(document);
    return;
  }

  var manager = { initScope: initScope, destroyScope: destroyScope };
  window[MANAGER_KEY] = manager;
  document.addEventListener('shopify:section:load', function(event) {
    manager.initScope(event.target);
  });
  document.addEventListener('shopify:section:unload', function(event) {
    manager.destroyScope(event.target);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      manager.initScope(document);
    }, { once: true });
  } else {
    manager.initScope(document);
  }
})();
